import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    TopToolbar,
    ExportButton,
    Filter,
    DateInput,
    SelectInput,
    useDataProvider,
    useNotify
} from 'react-admin';
import { Tabs, Tab, Box, Switch, FormControlLabel, Typography } from '@mui/material';
import { getTenantApiUrl } from '../utils/tenant';

// Format timestamp to readable date/time
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';

    // Parse timestamp format: 20250914-143028.374
    const match = timestamp.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})\.(\d{3})$/);
    if (!match) return timestamp;

    const [, year, month, day, hour, minute, second, millisecond] = match;
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}Z`);

    return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
    });
};

// Custom exporter for system logs
const systemLogsExporter = (records) => {
    const csvData = records.map(record => {
        return {
            timestamp: formatTimestamp(record.timestamp),
            message: record.message,
            team_id: record.team_id
        };
    });

    // Convert to CSV
    const csvContent = convertToCSV(csvData);
    downloadCSV(csvContent, 'system-logs');
};

// Helper function to convert data to CSV
const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [
        headers.join(','),
        ...data.map(row =>
            headers.map(header => {
                const value = row[header];
                // Escape commas and quotes in CSV values
                if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value || '';
            }).join(',')
        )
    ];

    return csvRows.join('\n');
};

// Helper function to download CSV
const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Filter component for logs
const LogsFilter = () => (
    <Filter>
        <DateInput
            source="date_from"
            label="From Date"
        />
        <DateInput
            source="date_to"
            label="To Date"
        />
        <SelectInput
            source="level"
            label="Log Level"
            choices={[
                { id: 'info', name: 'Info' },
                { id: 'warning', name: 'Warning' },
                { id: 'error', name: 'Error' },
                { id: 'debug', name: 'Debug' }
            ]}
            emptyText="All levels"
        />
    </Filter>
);

// List actions component with export button
const ListActions = () => (
    <TopToolbar>
        <ExportButton exporter={systemLogsExporter} />
    </TopToolbar>
);

// Custom List component for usage logs
const UsageLogsList = () => {
    return (
        <List
            resource="systemLogs"
            sort={{ field: 'timestamp', order: 'DESC' }}
            bulkActionButtons={false}
            title={false}
            actions={<ListActions />}
            filters={<LogsFilter />}
        >
            <Datagrid
                bulkActionButtons={false}
                sx={{
                    '& [data-field="__selection__"]': { display: 'none' },
                    '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                    '& .MuiTableCell-head': {
                        fontWeight: 'bold',
                    }
                }}
            >
                <FunctionField
                    source="timestamp"
                    label="Timestamp"
                    sortable={false}
                    render={record => formatTimestamp(record.timestamp)}
                />
                <TextField
                    source="message"
                    label="Message"
                    sortable={false}
                    sx={{
                        '& .MuiTableCell-root': {
                            maxWidth: '500px',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap'
                        }
                    }}
                />
            </Datagrid>
        </List>
    );
};

// Custom List component for combined logs
const CombinedLogsList = () => {
    return (
        <List
            resource="combinedLogs"
            sort={{ field: 'timestamp', order: 'DESC' }}
            bulkActionButtons={false}
            title={false}
            actions={<ListActions />}
            filters={<LogsFilter />}
        >
            <Datagrid
                bulkActionButtons={false}
                sx={{
                    '& [data-field="__selection__"]': { display: 'none' },
                    '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                    '& .MuiTableCell-head': {
                        fontWeight: 'bold',
                    }
                }}
            >
                <FunctionField
                    source="timestamp"
                    label="Timestamp"
                    sortable={false}
                    render={record => formatTimestamp(record.timestamp)}
                />
                <TextField
                    source="message"
                    label="Message"
                    sortable={false}
                    sx={{
                        '& .MuiTableCell-root': {
                            maxWidth: '500px',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap'
                        }
                    }}
                />
            </Datagrid>
        </List>
    );
};

// Debug Log Toggle Component
const DebugLogToggle = () => {
    const [debugLogEnabled, setDebugLogEnabled] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    // Get current team ID from localStorage
    const teamId = localStorage.getItem('userTeamId');

    // Fetch current debug log setting on component mount
    React.useEffect(() => {
        const fetchDebugLogSetting = async () => {
            if (!teamId) return;

            try {
                const response = await dataProvider.getList('settings', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'key', order: 'ASC' },
                    filter: {}
                });

                const logDebugSetting = response.data.find(setting => setting.key === 'logdebug');
                if (logDebugSetting) {
                    setDebugLogEnabled(logDebugSetting.value === 'true');
                }
            } catch (error) {
                console.error('Error fetching debug log setting:', error);
            }
        };

        fetchDebugLogSetting();
    }, [teamId, dataProvider]);

    const handleToggle = async (event) => {
        if (!teamId) {
            notify('No team ID found', { type: 'error' });
            return;
        }

        const newValue = event.target.checked;
        setLoading(true);

        try {
            // Make PUT request to update team settings using proper API URL
            const apiUrl = getTenantApiUrl(`teams/${teamId}/settings`);
            await fetch(apiUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    logdebug: newValue.toString()
                })
            });

            setDebugLogEnabled(newValue);
            notify(`Debug logging ${newValue ? 'enabled' : 'disabled'}`, { type: 'success' });
        } catch (error) {
            console.error('Error updating debug log setting:', error);
            notify('Failed to update debug log setting', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormControlLabel
            control={
                <Switch
                    checked={debugLogEnabled}
                    onChange={handleToggle}
                    disabled={loading}
                    size="small"
                />
            }
            label={
                <Typography variant="body2" color="textSecondary">
                    Debug log on/off
                </Typography>
            }
            sx={{ margin: 0 }}
        />
    );
};

// Main component with tabs
export const SystemLogsList = () => {
    const [tabValue, setTabValue] = React.useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ p: 2, pb: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0 }}>System Logs</h2>
                    <DebugLogToggle />
                </Box>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Usage Logs" />
                    <Tab label="Combined Logs" />
                </Tabs>
            </Box>

            <Box sx={{ mt: 2 }}>
                {tabValue === 0 && <UsageLogsList />}
                {tabValue === 1 && <CombinedLogsList />}
            </Box>
        </Box>
    );
};